import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';

/**
 * Custom BibTeX color themes for light and dark modes
 * Provides enhanced syntax highlighting colors for BibTeX entries
 */

// Light theme colors for BibTeX
const lightBibtexHighlight = HighlightStyle.define([
  { tag: t.keyword, color: '#0066cc', fontWeight: 'bold' }, // @article, @book (blue, bold)
  { tag: t.atom, color: '#116329', fontWeight: 'bold' }, // Citation key (green, bold)
  { tag: t.propertyName, color: '#6f42c1' }, // Field names like 'author', 'title' (purple)
  { tag: t.string, color: '#d73a49' }, // Field values in quotes/braces (red)
  { tag: t.number, color: '#005cc5' }, // Years and numeric values (blue)
  { tag: t.operator, color: '#24292e' }, // = sign (dark gray)
  { tag: t.punctuation, color: '#24292e' }, // Commas (dark gray)
  { tag: t.bracket, color: '#0066cc', fontWeight: 'bold' }, // Braces (blue, bold)
  { tag: t.comment, color: '#6a737d', fontStyle: 'italic' }, // Comments (gray, italic)
  { tag: t.variableName, color: '#e36209' }, // String variables (orange)
  { tag: t.invalid, color: '#cb2431', textDecoration: 'underline wavy' } // Invalid syntax (red, wavy underline)
]);

// Dark theme colors for BibTeX (Catppuccin Mocha inspired)
const darkBibtexHighlight = HighlightStyle.define([
  { tag: t.keyword, color: '#89b4fa', fontWeight: 'bold' }, // @article, @book (blue, bold)
  { tag: t.atom, color: '#a6e3a1', fontWeight: 'bold' }, // Citation key (green, bold)
  { tag: t.propertyName, color: '#cba6f7' }, // Field names like 'author', 'title' (mauve)
  { tag: t.string, color: '#f38ba8' }, // Field values in quotes/braces (red/pink)
  { tag: t.number, color: '#89dceb' }, // Years and numeric values (sky blue)
  { tag: t.operator, color: '#cdd6f4' }, // = sign (text color)
  { tag: t.punctuation, color: '#cdd6f4' }, // Commas (text color)
  { tag: t.bracket, color: '#89b4fa', fontWeight: 'bold' }, // Braces (blue, bold)
  { tag: t.comment, color: '#6c7086', fontStyle: 'italic' }, // Comments (overlay0, italic)
  { tag: t.variableName, color: '#fab387' }, // String variables (peach)
  { tag: t.invalid, color: '#f38ba8', textDecoration: 'underline wavy' } // Invalid syntax (red, wavy underline)
]);

// Additional editor styling for light theme
const lightBibtexTheme = EditorView.theme({
  '&': {
    backgroundColor: '#ffffff',
    color: '#24292e'
  },
  '.cm-content': {
    caretColor: '#0066cc',
    fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", "Courier New", monospace'
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: '#0066cc'
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: '#c8e1ff'
  },
  '.cm-activeLine': {
    backgroundColor: '#f6f8fa'
  },
  '.cm-gutters': {
    backgroundColor: '#f6f8fa',
    color: '#6a737d',
    border: 'none'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#e1e4e8'
  }
}, { dark: false });

// Additional editor styling for dark theme (Catppuccin Mocha)
const darkBibtexTheme = EditorView.theme({
  '&': {
    backgroundColor: '#181825', // --ctp-mantle (darker default background)
    color: '#cdd6f4'
  },
  '.cm-content': {
    caretColor: '#89b4fa',
    fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", "Courier New", monospace',
    backgroundColor: '#181825' // Ensure content area is also dark
  },
  '.cm-line': {
    backgroundColor: 'transparent' // All lines use the dark background by default
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: '#89b4fa'
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: '#45475a'
  },
  '.cm-activeLine': {
    backgroundColor: '#1e1e2e' // Active line is slightly lighter (--ctp-base)
  },
  '.cm-gutters': {
    backgroundColor: '#11111b', // --ctp-crust (even darker for gutters)
    color: '#6c7086',
    border: 'none'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#1e1e2e' // Match active line background
  }
}, { dark: true });

// Export combined themes
export const lightBibtexThemeExtension = [
  lightBibtexTheme,
  syntaxHighlighting(lightBibtexHighlight)
];

export const darkBibtexThemeExtension = [
  darkBibtexTheme,
  syntaxHighlighting(darkBibtexHighlight)
];
